/* eslint-disable import/no-cycle */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { createUseStyles } from '@glu/theming';
import classnames from 'classnames';

import memoize from 'lodash/memoize';
import values from 'lodash/values';
import get from 'lodash/get';
import locale from '@glu/locale';

import {
  DatePicker as ValidatedDatePicker,
  defaultStrings_en_US as defaultStrings
} from '@glu/datepicker-react';
import { withFormContext } from '@glu/validation-react';
import PlaceResolver, { variants } from '../../placeResolver/PlaceResolver';

import { DATE_FORMAT, API_DATE_FORMAT, PAYMENT_TYPES } from '../../../constants';
import { getPaymentType } from '../../createPayment/CreatePayment.utils';
import LocaleContext from '../../locale/LocaleContext';

import { getAvailableBusinessDays } from '../../../api/payments/service';

const FormContextWrapper = ({ children, data, validate }) =>
  children({ formData: data, gluValidate: validate });
const FormContext = withFormContext(FormContextWrapper);

locale.set(defaultStrings);

const styles = () => ({
  international: {
    '& label[for=transactionDate]': {
      margin: 0
    }
  },
  domestic: {
    '& label[for=transactionDate]': {
      marginBottom: 20
    }
  }
});

const useStyles = createUseStyles(styles);

const getBusinessDays = memoize(getAvailableBusinessDays, (...args) => values(args[0]).join('_'));

const TransactionDateComponent = ({ data, formState, fromTemplate, isTemplate }) => {
  const locale = useContext(LocaleContext);
  const classes = useStyles();
  const domestic =
    get(
      formState,
      'paymentType[0].type',
      getPaymentType(formState, data.paymentTypeOptions, { template: isTemplate })
    ) === PAYMENT_TYPES.WIRE.DOMESTIC;

  const currency =
    get(formState, 'currency.0.currency') ||
    get(formState, 'currency.currency', get(formState, 'currency'));

  const type = get(
    formState,
    'paymentType[0].type',
    getPaymentType(formState, data.paymentTypeOptions)
  );

  if (!type || (!domestic && !currency)) {
    return null;
  }
  return (
    <FormContext>
      {({ formData, gluValidate }) => {
        return (
          <PlaceResolver
            services={[() => getBusinessDays({ paymentMethod: type.toLowerCase(), currency })]}
            variant={variants.spinner}
          >
            {({ results: [businessDays] }) => {
              const base = fromTemplate ? businessDays : formState.transactionDate || businessDays;
              let transactionDate;
              if (typeof formData.transactionDate === 'string')
                transactionDate = moment(formData.transactionDate);
              if (formData.transactionDate && typeof formData.transactionDate !== 'string')
                transactionDate = moment(formData.transactionDate[0]);
              if (typeof formData.transactionDate === 'undefined')
                transactionDate = moment(businessDays[0]);
              const startDate = transactionDate.isBefore(moment(businessDays[0]))
                ? businessDays[0]
                : moment.parseZone(base[0]).startOf('day');

              return (
                <ValidatedDatePicker
                  htmlId="transactionDate"
                  name="transactionDate"
                  className={classnames({
                    [classes.domestic]: domestic,
                    [classes.international]: !domestic
                  })}
                  defaultValue={[startDate, startDate]}
                  autoApply
                  dataQa="transactionDate"
                  format={DATE_FORMAT}
                  labelText={locale['common.transactionDate']}
                  isSelectableDate={(potentialDate) => {
                    const formattedDate = potentialDate.startOf('day').format(API_DATE_FORMAT);
                    const days = businessDays.map((x) =>
                      moment.parseZone(x).format(API_DATE_FORMAT)
                    );
                    return !days.includes(formattedDate);
                  }}
                  onChange={() => {
                    if (formState.amount || formData.amount) {
                      gluValidate('amount');
                    }
                  }}
                  validators={[{ required: true, msg: locale['validation.required'] }]}
                  singleDatePicker
                />
              );
            }}
          </PlaceResolver>
        );
      }}
    </FormContext>
  );
};

TransactionDateComponent.propTypes = {
  formState: PropTypes.shape({}).isRequired,
  services: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({}).isRequired,
  isTemplate: PropTypes.bool.isRequired,
  fromTemplate: PropTypes.bool.isRequired
};

export default TransactionDateComponent;
